import type { SwimlaneLayoutDTO, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { useMemo } from 'react';
import { useUniqueHeaderValues } from '@/components/loop-visualizations/layout/colors-panel/hooks/useUniqueHeaderValues';
import {
  isHeaderDate,
  MULTI_VALUE_CUSTOM_FIELD_TYPES,
} from '@/components/loop-visualizations/layout/header-menu/utils';
import { resolveSwimlaneColors } from '@/components/views/swimlane/utils/resolveSwimlaneColors';
import { useVisualizationHeaderOptions } from '@/components/views/utils/useVisualizationHeaderOptions';
import { getHeaderFieldId } from '@/components/views/utils/visualizationHeaderUtils';
import type { ItemV2 } from '@/lib/api/types/items';
import type {
  IPropertySelectorByID,
  ISwimlaneHeader,
  ItemStatusOption,
  ItemTypeLevelOption,
  ItemTypeOption,
} from '@/lib/api/types/loop-visualization';
import { useGetCustomFieldsQuery } from '@/lib/queries/common/useGetCustomFieldsQuery';
import { getPaletteSwatches } from '@/lib/utils/loop-visualization/colors';
import { headerToPropertySelector } from '@/lib/utils/loop-visualization/commonLayoutUtils';
import { getAllItemFieldValues } from '@/lib/utils/loop-visualization/fieldExtraction';
import { useVisualizationColorItems } from './useVisualizationColorItems';
import { useVisualizationLabelMap } from './useVisualizationLabelMap';

type VisualizationLayout = SwimlaneLayoutDTO | TimelineLayoutDTO | null | undefined;

interface VisualizationHeaders {
  header1: IPropertySelectorByID | null;
  header2: IPropertySelectorByID | null;
  groupRowsByHeader?: IPropertySelectorByID | null;
  itemLevelId: number;
}

interface UseVisualizationColorDataParams {
  layout: VisualizationLayout;
  headers: VisualizationHeaders | undefined;
  items: ItemV2[];
  itemTypes: ItemTypeOption[];
  itemLevels: ItemTypeLevelOption[];
  itemStatuses: ItemStatusOption[];
  noValueLabel: string;
  columnHeaderOrder?: string[];
  rowHeaderOrder?: string[];
  enabled?: boolean;
}

export function useVisualizationColorData({
  layout,
  headers,
  items,
  itemTypes,
  itemLevels,
  itemStatuses,
  noValueLabel,
  columnHeaderOrder,
  rowHeaderOrder,
  enabled = true,
}: UseVisualizationColorDataParams) {
  const colorConfig = layout?.colorConfig;

  const { data: customFields } = useGetCustomFieldsQuery({ enabled });
  const multiValueFieldIds = useMemo(
    () =>
      new Set(
        (customFields ?? [])
          .filter((f) =>
            MULTI_VALUE_CUSTOM_FIELD_TYPES.includes(
              f.type as (typeof MULTI_VALUE_CUSTOM_FIELD_TYPES)[number],
            ),
          )
          .map((f) => f.id.toString()),
      ),
    [customFields],
  );

  const itemLevelId = headers?.itemLevelId ?? 0;

  const visualizationHeader1: ISwimlaneHeader | null = useVisualizationHeaderOptions(
    headers?.header1 ?? null,
    items,
    itemLevelId,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      enabled,
    },
  );

  const visualizationHeader2: ISwimlaneHeader | null = useVisualizationHeaderOptions(
    headers?.header2 ?? null,
    items,
    itemLevelId,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      enabled,
    },
  );

  const visualizationGroupRowsByHeader: ISwimlaneHeader | null = useVisualizationHeaderOptions(
    headers?.groupRowsByHeader ?? null,
    items,
    itemLevelId,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      enabled,
    },
  );

  const header1FieldId = useMemo(
    () => getHeaderFieldId(visualizationHeader1) || null,
    [visualizationHeader1],
  );

  const header2FieldId = useMemo(
    () => getHeaderFieldId(visualizationHeader2) || null,
    [visualizationHeader2],
  );

  const groupRowsByFieldId = useMemo(
    () => getHeaderFieldId(visualizationGroupRowsByHeader) || null,
    [visualizationGroupRowsByHeader],
  );

  const defaultColorByField = useMemo(() => {
    if (
      header1FieldId &&
      !isHeaderDate(header1FieldId) &&
      !multiValueFieldIds?.has(header1FieldId)
    ) {
      return header1FieldId;
    }
    if (
      header2FieldId &&
      !isHeaderDate(header2FieldId) &&
      !multiValueFieldIds?.has(header2FieldId)
    ) {
      return header2FieldId;
    }
    return 'status';
  }, [header1FieldId, header2FieldId, multiValueFieldIds]);

  const storedColorByField = colorConfig?.colorByField;
  const currentColorByField =
    storedColorByField && !multiValueFieldIds?.has(storedColorByField)
      ? storedColorByField
      : defaultColorByField;
  const isBaseColorSelected = colorConfig?.mode === 'baseColor';

  const colorByHeaderSelector = useMemo<IPropertySelectorByID | null>(() => {
    if (isBaseColorSelected || !currentColorByField) {
      return null;
    }

    try {
      return headerToPropertySelector(currentColorByField);
    } catch {
      return null;
    }
  }, [currentColorByField, isBaseColorSelected]);

  const colorByFieldHeader: ISwimlaneHeader | null = useVisualizationHeaderOptions(
    colorByHeaderSelector,
    items,
    itemLevelId,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      enabled,
    },
  );

  const matchingHeader = useMemo(() => {
    if (header1FieldId === currentColorByField) return visualizationHeader1;
    if (header2FieldId === currentColorByField) return visualizationHeader2;
    if (groupRowsByFieldId === currentColorByField) return visualizationGroupRowsByHeader;
    return null;
  }, [
    header1FieldId,
    header2FieldId,
    groupRowsByFieldId,
    currentColorByField,
    visualizationHeader1,
    visualizationHeader2,
    visualizationGroupRowsByHeader,
  ]);

  const headerForColorField = matchingHeader ?? colorByFieldHeader;

  const colorByFieldValues = useUniqueHeaderValues(
    items,
    currentColorByField,
    isBaseColorSelected,
    matchingHeader,
    headerForColorField,
    layout?.showEmptyHeaders,
  );

  const currentHeaderColorOrder = colorConfig?.headerColorOrder || [];

  const paletteSwatches = useMemo(() => getPaletteSwatches(colorConfig), [colorConfig]);

  let displayOrder: string[] | undefined;
  if (currentColorByField === header1FieldId) {
    displayOrder = columnHeaderOrder;
  } else if (currentColorByField === header2FieldId) {
    displayOrder = rowHeaderOrder;
  }

  const resolvedColors = useMemo(
    () =>
      resolveSwimlaneColors({
        allKnownValues: colorByFieldValues,
        headerColorOrder: currentHeaderColorOrder,
        displayOrder,
      }),
    [colorByFieldValues, currentHeaderColorOrder, displayOrder],
  );

  const labelMap = useVisualizationLabelMap({
    headerForColorField: headerForColorField?.type === 'DateSelector' ? null : headerForColorField,
    currentColorByField,
    uniqueValues: colorByFieldValues,
    itemsForIdLookup: items,
    noValueLabel,
  });

  const colorItems = useVisualizationColorItems({
    orderedFieldValues: resolvedColors.stableOrderedValues,
    colorConfig,
    paletteSwatches,
    labelMap,
    valueToColorIndex: resolvedColors.valueToColorIndex,
  });

  const isColorByFieldAHeader =
    Boolean(currentColorByField) &&
    (currentColorByField === header1FieldId ||
      currentColorByField === header2FieldId ||
      currentColorByField === groupRowsByFieldId);

  // When showEmptyHeaders is off, strip colour items whose values don't appear in any
  // visible item so the legend matches what the swimlane/timeline actually renders.
  const visibleColorItems = useMemo(() => {
    if (
      !currentColorByField ||
      items.length === 0 ||
      (isColorByFieldAHeader && layout?.showEmptyHeaders)
    ) {
      return colorItems;
    }

    const valuesInItems = new Set<string>();
    let hasItemWithNoFieldValues = false;
    for (const item of items) {
      const fieldValues = getAllItemFieldValues(item, currentColorByField);
      if (fieldValues.length === 0) {
        hasItemWithNoFieldValues = true;
      }
      for (const v of fieldValues) {
        valuesInItems.add(v);
      }
    }
    if (hasItemWithNoFieldValues) {
      valuesInItems.add('null');
    }

    const filtered = colorItems.filter((ci) => valuesInItems.has(ci.id));
    return filtered.length === 0 ? colorItems : filtered;
  }, [colorItems, items, currentColorByField, isColorByFieldAHeader, layout?.showEmptyHeaders]);

  return {
    layout,
    colorConfig,
    items,
    allItemsWithParents: items,
    visualizationHeader1,
    visualizationHeader2,
    header1FieldId,
    header2FieldId,
    defaultColorByField,
    currentColorByField,
    isBaseColorSelected,
    isColorByFieldAHeader,
    matchingHeader,
    headerForColorField,
    colorByFieldValues,
    currentHeaderColorOrder,
    paletteSwatches,
    labelMap,
    colorItems: visibleColorItems,
    stableOrderedValues: resolvedColors.stableOrderedValues,
    valueToColorIndex: resolvedColors.valueToColorIndex,
  };
}
